import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import LoadingButton from '@mui/lab/LoadingButton';
import DrawIcon from '@mui/icons-material/DrawRounded';
import SendIcon from '@mui/icons-material/SendRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';

import { isEmailValid } from 'src/utils/is-email-valid';
import { isMobileUser } from 'src/common/utils';
import { EMessageChannel, useUpdateLead} from 'src/services/api';
import ThemeProvider from 'src/theme/ThemeProvider';
import { Flex, FlexColumn } from 'src/components/flex';
import TextField from 'src/components/input/text-field/TextField';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import SelectChannelButton from 'src/pages/landlord/leads/messages/components/input/SelectChannelButton';
import { isSupportedSignatureChannel } from 'src/services/api/users/settings/types';
import FileAttachments from 'src/pages/landlord/leads/messages/components/input/FileAttachments';
import { SnippetsIcon } from 'src/icons';

const MMS_MAX_ATTACHMENT_COUNT = 10;
const MMS_TOTAL_ATTACHMENT_SIZE_LIMIT = 4_500_000;

const getTotalFileSize = (files: File[]) => files.reduce((total, file) => total + file.size, 0);

const getSmsAttachmentsError = (files: File[]) => {
    if (files.length > MMS_MAX_ATTACHMENT_COUNT) {
        return `MMS supports at most ${MMS_MAX_ATTACHMENT_COUNT} attachments.`;
    }

    if (getTotalFileSize(files) > MMS_TOTAL_ATTACHMENT_SIZE_LIMIT) {
        return 'MMS attachments must be 4.5 MB or less in total.';
    }

    return '';
};

/**
 * The pane used to reply to messages from a lead.
 * Includes surrounding UI elements like the channel selector, smart replies, and keyboard shortcuts sections.
 */
export default function LeadMessagesInput() {
    const {
        loggedInUser,
        userSettings,
        channel,
        lead,
        replyInput,
        setReplyInput,
        sendMessage,
        isSendingMessage,
        setIsSnippetsModalOpen,
    } = useLeadMessagesPageContext();
    const { mutate: updateLead, isLoading: isUpdatingLead } = useUpdateLead(lead.uuid);
    const assignLeadToSelf = async () => {
        updateLead({
            // uuid, name, email, and phone_number are required for the request, even though we are not changing them
            uuid: lead.uuid,
            name: lead.name,
            email: lead.email,
            phone_number: lead.phone_number,
            property_id: lead.property_id,
            owner: loggedInUser.id
        });
    };

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const signature = isSupportedSignatureChannel(channel) ? userSettings.signatures[channel] : undefined;
    const isSignatureEnabled = signature?.is_enabled;
    const formattedSignature = isSignatureEnabled ? `\n---\n${signature.text}` : '';

    const isOwnLead = loggedInUser.id === lead.owner;
    const isEmailChannelAndEmailIsInvalid = channel === EMessageChannel.EMAIL && !isEmailValid(lead.email);

    const missingChannelMessage =
        (channel === EMessageChannel.SMS && !loggedInUser.lead_contact?.sms_phone_number) ?
            'You are trying to send an SMS but do not have an SMS phone number set up.': '';

    const isMissingChannelSetup = !!missingChannelMessage;

    const isInputDisabled = isEmailChannelAndEmailIsInvalid || !isOwnLead || isMissingChannelSetup;

    const isAttachFilesEnabled = [EMessageChannel.EMAIL, EMessageChannel.FACEBOOK_MESSENGER, EMessageChannel.SMS].includes(channel);

    const [files, setFiles] = useState<File[]>([]);
    const [attachmentsError, setAttachmentsError] = useState('');
    const hasFileAttachments = files.length > 0;
    const smsAttachmentsError = channel === EMessageChannel.SMS ? getSmsAttachmentsError(files) : '';
    const currentAttachmentsError = channel === EMessageChannel.SMS ? smsAttachmentsError || attachmentsError : '';

    // empty means that the input is empty or contains only the signature
    const isEmptyInput = !replyInput.trim() || replyInput.trim() === formattedSignature.trim();

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const isEnterPressed = event.key === 'Enter';
        const isHoldingShift = event.shiftKey;
        const isEnterWithoutShiftPressed = isEnterPressed && !isHoldingShift;

        if (isEnterWithoutShiftPressed && isEmptyInput) {
            event.preventDefault();
            return;
        }

        if (isEnterWithoutShiftPressed && !isEmptyInput && !isMobileUser()) {
            handleSendReply();
            event.preventDefault();
        }
    };

    const handleSendReply = () => {
        if (isSendingMessage) { return; }
        if (smsAttachmentsError) {
            setAttachmentsError(smsAttachmentsError);
            return;
        }

        sendMessage(replyInput, files);
        setReplyInput(formattedSignature);
        setFiles([]);
        setAttachmentsError('');
        // set the cursor to the beginning of the input
        setTimeout(() => inputRef.current?.setSelectionRange(0, 0), 0);
    };

    const toggleSignature = () => {
        if (!formattedSignature) {
            return;
        }

        if (replyInput.includes(formattedSignature)) {
            setReplyInput(replyInput.replace(formattedSignature, ''));
        } else {
            setReplyInput(replyInput + formattedSignature);
        }
    };

    const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const res: File[] = [];
            [...e.target.files].forEach((file) => {
                const isFileAlreadyAttached = res.some((f) => f.name === file.name);
                if (isFileAlreadyAttached) {
                    return;
                }
                res.push(file);
            });
            setFiles(prevState => {
                const nextFiles = [...prevState, ...res];
                const nextError = channel === EMessageChannel.SMS ? getSmsAttachmentsError(nextFiles) : '';
                setAttachmentsError(nextError);
                return nextError ? prevState : nextFiles;
            });
        }
        e.target.value = '';
    };

    const handleSetFiles: React.Dispatch<React.SetStateAction<File[]>> = (value) => {
        setFiles((prevFiles) => {
            const nextFiles = typeof value === 'function' ? value(prevFiles) : value;
            setAttachmentsError(channel === EMessageChannel.SMS ? getSmsAttachmentsError(nextFiles) : '');
            return nextFiles;
        });
    };

    // set the reply input to the signature when the signature loads
    // or when the lead changes
    useEffect(() => {
        setReplyInput(formattedSignature);
    }, [formattedSignature, lead.uuid]);

    if (!isOwnLead) {
        return (
            <Box p={2}>
                <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                        <LoadingButton
                            variant="contained"
                            color="warning"
                            onClick={assignLeadToSelf}
                            size="small"
                            loading={isUpdatingLead}
                            sx={{ color: 'white' }}
                        >
                            Assign lead to yourself
                        </LoadingButton>
                    }
                >
                    This lead does not belong to you so you cannot send a reply.
                </Alert>
            </Box>
        );
    }

    if (isMissingChannelSetup) {
        return (
            <Box p={2}>
                <Alert severity="error" variant="outlined">
                    {missingChannelMessage}{' '}
                    <a href={'/landlord/inner/settings'}>
                        Click here to set up SMS.
                    </a>
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={1}>
            <FlexColumn rowGap={1}>
                {hasFileAttachments && (
                    <FileAttachments
                        files={files}
                        setFiles={handleSetFiles}
                    />
                )}
                {currentAttachmentsError && (
                    <Alert severity="error" variant="outlined">
                        {currentAttachmentsError}
                    </Alert>
                )}

                <TextField
                    inputRef={inputRef}
                    autoComplete="off"
                    placeholder="Write your reply here"
                    multiline
                    minRows={4}
                    maxRows={12}
                    value={replyInput}
                    onChange={setReplyInput}
                    onKeyDown={handleKeyDown}
                    disabled={isInputDisabled}
                    fullWidth
                    inputProps={{
                        maxLength: channel === EMessageChannel.SMS ? 1000 : undefined
                    }}
                    InputProps={{
                        endAdornment: (
                            <ThemeProvider>
                                <Flex
                                    position="absolute"
                                    bottom={0}
                                    left="8px"
                                    right="32px"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    columnGap={1}
                                    sx={{ backgroundColor: 'white' }}
                                >
                                    <SelectChannelButton />

                                    <Tooltip title="Select snippet">
                                        <div>
                                            <IconButton
                                                onClick={() => setIsSnippetsModalOpen(true)}
                                                data-testid="select-snippet-button"
                                            >
                                                <SnippetsIcon />
                                            </IconButton>
                                        </div>
                                    </Tooltip>

                                    {/* signature button */}
                                    <Tooltip title={!isSignatureEnabled ? 'Signature is not enabled' : 'Toggle signature'}>
                                        <div>
                                            <IconButton
                                                onClick={toggleSignature}
                                                disabled={!isSignatureEnabled}
                                                sx={{ p: 0.5 }}
                                                data-testid="signature-button"
                                            >
                                                <DrawIcon />
                                            </IconButton>
                                        </div>
                                    </Tooltip>

                                    {/* attach files button */}
                                    <Tooltip title={!isAttachFilesEnabled ? 'Attachments are not supported for this channel' : channel === EMessageChannel.SMS ? 'Attach files (MMS max 10 files, 4.5 MB total)' : 'Attach files'}>
                                        <FormControl>
                                            <label>
                                                <Input
                                                    sx={{ display: 'none'}}
                                                    type="file"
                                                    inputProps={{ multiple: true }}
                                                    onChange={handleUploadFile}
                                                    disabled={!isAttachFilesEnabled}
                                                />
                                                <IconButton
                                                    component="span"
                                                    disabled={!isAttachFilesEnabled}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <AttachFileIcon />
                                                </IconButton>
                                            </label>
                                        </FormControl>
                                    </Tooltip>

                                    {/* character counter for SMS */}
                                    {channel === EMessageChannel.SMS && (
                                        <Box
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: replyInput.length > 160 ? 'error.main' : 'text.secondary',
                                                minWidth: '40px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {replyInput.length}/1000
                                        </Box>
                                    )}

                                    {/* send button */}
                                    <LoadingButton
                                        variant="text"
                                        onClick={handleSendReply}
                                        endIcon={<SendIcon />}
                                        loading={isSendingMessage}
                                        disabled={isEmptyInput || isInputDisabled || !!smsAttachmentsError}
                                        loadingPosition="end"
                                        size="small"
                                        sx={{ p: 0.5 }}
                                        data-testid="send-message-button"
                                    >
                                        <span>Send</span>
                                    </LoadingButton>
                                </Flex>
                            </ThemeProvider>
                        ),
                        sx: { paddingBottom: '40px' }
                    }}
                />
            </FlexColumn>
        </Box>
    );
}
